import { css } from '@emotion/react';
import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

const radiosCss = css`
  gap: 0.25rem;
`;

export interface DescribedRadioOption<T extends string> {
  value: T;
  label: ReactNode;
  /** The sentence under the label that says what choosing this option means. */
  description?: ReactNode;
}

interface DescribedRadioGroupProps<T extends string> {
  /** Names the group for assistive tech; the options' labels name each radio. */
  ariaLabel: string;
  options: DescribedRadioOption<T>[];
  /** Null selects nothing. */
  value: T | null;
  onChange: (next: T) => void;
}

/**
 * A choice between named options, each carrying the sentence that explains it — the
 * radio-with-caption shape the config pages and dialogs share.
 */
export function DescribedRadioGroup<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
}: DescribedRadioGroupProps<T>) {
  return (
    <RadioGroup
      aria-label={ariaLabel}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value as T)}
      css={radiosCss}
    >
      {options.map((option) => (
        <FormControlLabel
          key={option.value}
          value={option.value}
          control={<Radio size='small' />}
          sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
          label={
            <Stack>
              <Typography variant='body2'>{option.label}</Typography>
              {option.description && (
                <Typography variant='caption' color='text.secondary'>
                  {option.description}
                </Typography>
              )}
            </Stack>
          }
        />
      ))}
    </RadioGroup>
  );
}
